import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ROUTES } from "@/shared/config/routes";
import useCredit4uDesyncStore, {
  suppressCredit4uDesyncNotice,
  isCredit4uDesyncNoticeSuppressed,
} from "@/shared/auth/credit4uDesyncStore";
import { getCurrentUserKey } from "@/shared/auth/tokenService";
import Credit4uOutOfSyncSheet from "./Credit4uOutOfSyncSheet";

/**
 * credit4u desync(OUT_OF_SYNC) 미복구 상태가 남아 있으면 홈(`/main`) 진입 시 재설정 유도
 * 시트를 다시 띄우는 Router 레벨 전역 호스트.
 *
 * `Credit4uDesyncCsRecovery`(DESYNC → CS 문의 팝업)와는 다른 컴포넌트다 — 혼동 주의.
 *
 * ## 오버레이 무충돌 근거
 *
 * 만료 시트(`Credit4uPasswordExpiryRecovery`)와 동시 노출이 불가능한 이유: 만료 시트는
 * arm 된 세대의 재동기화 잡에서만 뜨고, 잡을 시작하는 유일한 수단은 홈 새로고침 버튼이다.
 * 이 시트가 열려 있으면 잠긴 backdrop 때문에 그 버튼을 누를 수 없다. 역방향도 불가 — 홈은
 * 마운트만으로 잡을 돌리지 않는다. 이 논증은 `Credit4uOutOfSyncSheet` 의 잠금 config
 * (`closeOnBackdrop:false` / `closeOnEsc:false` / `draggable:false` — native back 도 흔들림만)에
 * 의존하므로 그 config 를 완화하면 별도 우선순위 기전이 필요해진다.
 *
 * ## 열림/닫힘은 단일 파생식(`shouldOpen`)에서만 결정한다
 *
 * Router 레벨 마운트라 라우트 전환으로 언마운트되지 않는다. `/main` → 재설정 화면 이동 시
 * 시트가 남아 있으면 사용자의 자기 복구를 방해하므로, `shouldOpen` 이 false 로 바뀌는 모든
 * 경우에도 반드시 `setOpen(false)` 를 호출해야 한다("열기"만 있고 "닫기"가 없는 구조 금지).
 */
const Credit4uOutOfSyncRecovery = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const userKey = getCurrentUserKey();
  // selector 로 좁혀 구독 — 전체 state 를 구독하면 무관한 사용자의 pending 갱신에도 재렌더된다.
  const pending = useCredit4uDesyncStore((s) =>
    userKey ? (s.pendingByUser[userKey] ?? false) : false,
  );

  useEffect(() => {
    // `getCurrentUserKey()` 자체는 반응형이 아니지만, 로그인/로그아웃 전환은 반드시 라우트
    // 이동을 동반하므로 `location.pathname` 의존성으로 이 effect 가 재평가되어 최신 사용자
    // 기준으로 다시 판정된다.
    const shouldOpen =
      pending &&
      !isCredit4uDesyncNoticeSuppressed() &&
      location.pathname === ROUTES.MAIN;
    setIsOpen(shouldOpen);
  }, [pending, location.pathname]);

  const handleReset = () => {
    // 재설정을 시작하는 정상 경로 — suppress 를 호출하지 않는다(재설정 화면에서 이탈해도
    // pending 이 유지되는 한 홈 재진입 시 다시 안내되어야 한다).
    setIsOpen(false);
    // ⚠️ replace 가 아니라 push. CreditPasswordRenewalPage 는 갱신 화면(막다른 재시도 경로)을
    // 히스토리에서 지우려고 replace 를 쓰지만, 여기는 홈에서 진입하는 것이라 홈을 지울 이유가
    // 없다 — 뒤로가기로 홈에 돌아올 수 있어야 한다.
    navigate(ROUTES.CREDIT_PASSWORD_RESET);
  };

  const handleClose = () => {
    setIsOpen(false);
    // 영속 플래그(pending)는 지우지 않는다 — 앱을 다시 열면 반드시 다시 안내되어야 한다.
    // 이번 실행에서만 재노출을 접는다.
    suppressCredit4uDesyncNotice();
  };

  return (
    <Credit4uOutOfSyncSheet
      isOpen={isOpen}
      onReset={handleReset}
      onClose={handleClose}
    />
  );
};

export default Credit4uOutOfSyncRecovery;
